import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useStatuses } from "@/context/StatusContext";
import CourierTatManager from "@/components/CourierTatManager";
import { toast } from "sonner";
import { Save, Clock, Bell, ShieldCheck, ExternalLink, Truck, Plus, Trash2, MessageCircle, ListChecks, Globe, RefreshCw } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const SECTIONS = [
  { id: "general", label: "General", icon: Clock },
  { id: "overdue-tat", label: "Overdue & TAT", icon: Clock },
  { id: "couriers", label: "Couriers", icon: Truck },
  { id: "countries", label: "Countries", icon: Globe },
  { id: "final-actions", label: "Final actions", icon: ListChecks },
  { id: "shipment-status", label: "Shipment status", icon: ListChecks },
  { id: "roles", label: "Roles", icon: ShieldCheck },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { id: "alerts", label: "Alerts & Notifications", icon: Bell },
  { id: "microsoft", label: "Microsoft 365", icon: ShieldCheck },
];

export default function Settings() {
  const [days, setDays] = useState(5);
  const [company, setCompany] = useState("GirOrganic");
  const [teams, setTeams] = useState("");
  const [alertWithin, setAlertWithin] = useState(1);
  const [waTemplates, setWaTemplates] = useState({});
  const [fallbackUrl, setFallbackUrl] = useState("");
  const [origin, setOrigin] = useState("395010");
  const [countWorking, setCountWorking] = useState(false);
  const [weeklyOff, setWeeklyOff] = useState([6]);
  const [holidays, setHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [active, setActive] = useState("general");
  const [muteEmails, setMuteEmails] = useState(false);
  const { statuses, reload: reloadStatuses } = useStatuses();
  const markDirty = () => setDirty(true);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setDays(r.data.resolution_days);
      setCompany(r.data.company_name || "GirOrganic");
      setTeams(r.data.teams_webhook_url || "");
      setAlertWithin(r.data.alert_within_days ?? 1);
      setWaTemplates(r.data.wa_templates || {});
      setFallbackUrl(r.data.fallback_tracking_url || "");
      setOrigin(r.data.dispatch_origin_pincode || "395010");
      setCountWorking(!!r.data.count_working_days);
      setWeeklyOff(Array.isArray(r.data.weekly_off_days) ? r.data.weekly_off_days : [6]);
      setHolidays(Array.isArray(r.data.holidays) ? r.data.holidays : []);
      setDirty(false);
    });
    api.get("/notifications/prefs").then((r) => setMuteEmails(!!r.data.mute_alert_emails)).catch(() => {});
  }, []);

  const goTo = (id) => {
    setActive(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        resolution_days: Number(days), company_name: company,
        teams_webhook_url: teams, alert_within_days: Number(alertWithin),
        wa_templates: waTemplates, fallback_tracking_url: fallbackUrl,
        dispatch_origin_pincode: origin,
        count_working_days: countWorking, weekly_off_days: weeklyOff, holidays,
      });
      setDirty(false);
      toast.success("Settings saved. Resolution deadlines recalculated.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  const toggleEmails = async (emailOn) => {
    const muted = !emailOn;
    setMuteEmails(muted);
    try {
      await api.put("/notifications/email-alerts", { muted });
      toast.success(muted ? "Alert emails turned off for your account" : "Alert emails turned on");
    } catch {
      setMuteEmails(!muted);
      toast.error("Failed to update email preference");
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await api.post("/alerts/test");
      toast.success("Test alert dispatched (email to all users + Teams if configured).");
    } catch (e) {
      toast.error("Failed to send test alert");
    } finally { setTesting(false); }
  };

  return (
    <div className="max-w-4xl mx-auto pb-20">
      <div className="sticky top-16 z-10 -mx-8 px-8 pt-3 pb-2.5 bg-[#F7F7F4]/95 backdrop-blur border-b border-border">
        <div className="flex items-center justify-between gap-3">
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#111411] tracking-tight">Settings</h1>
          <div className="flex items-center gap-2">
            {dirty && (
              <span data-testid="settings-unsaved" className="hidden sm:inline text-xs font-medium rounded-full px-2.5 py-1 bg-[#FBEEE6] text-[#B45309] border border-[#F0D8C4]">
                Unsaved changes
              </span>
            )}
            <Button onClick={save} disabled={saving} data-testid="settings-save"
                    className="gap-2 bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">
              <Save className="h-4 w-4" /> Save changes
            </Button>
          </div>
        </div>
        <div className="mt-2.5 flex gap-1.5 overflow-x-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }}>
          {SECTIONS.map((sec) => {
            const Icon = sec.icon;
            return (
              <button key={sec.id} data-testid={`settings-nav-${sec.id}`} onClick={() => goTo(sec.id)}
                      className={`whitespace-nowrap text-xs font-medium rounded-full px-3 py-1.5 border transition-colors flex items-center gap-1.5 ${
                        active === sec.id ? "bg-[#0B3D3D] text-white border-[#0B3D3D]" : "bg-white text-muted-foreground border-border hover:border-[#0B3D3D]"
                      }`}>
                <Icon className="h-3.5 w-3.5 shrink-0" /> {sec.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-6 mt-6">
        <section id="general" className="scroll-mt-40 rounded-lg border border-border bg-white p-6 space-y-5">
          <div className="flex items-center gap-2 text-[#0B3D3D]">
            <Clock className="h-5 w-5" />
            <h3 className="font-heading font-bold text-[#111411]">Resolution timeframe</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Days from a shipment's dispatch (pickup) date within which the NDR team must resolve it. Drives the
            resolution deadline shown to agents.
          </p>
          <div className="max-w-xs">
            <Label>Days to resolve (from dispatch)</Label>
            <Input type="number" min={1} data-testid="settings-days" value={days}
                   onChange={(e) => { setDays(e.target.value); markDirty(); }} className="mt-1.5" />
          </div>
          <div className="max-w-xs">
            <Label>Company name</Label>
            <Input data-testid="settings-company" value={company} onChange={(e) => { setCompany(e.target.value); markDirty(); }} className="mt-1.5" />
          </div>
          <div className="max-w-xs">
            <Label>Dispatch origin PIN</Label>
            <Input data-testid="settings-origin" value={origin} onChange={(e) => { setOrigin(e.target.value); markDirty(); }} className="mt-1.5" placeholder="e.g. 395010" />
            <p className="text-[11px] text-muted-foreground mt-1">Where you ship from. Courier TAT zones are relative to this origin.</p>
          </div>
        </section>

        <section id="overdue-tat" className="scroll-mt-40 rounded-lg border border-border bg-white p-6 space-y-4">
          <div className="flex items-center gap-2 text-[#0B3D3D]">
            <Clock className="h-5 w-5" />
            <h3 className="font-heading font-bold text-[#111411]">Courier delivery TAT (overdue rules)</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Per courier, set zone → delivery days and upload the PIN → zone list. Overdue is judged by <b>Initial EDD</b> first,
            else the courier <b>TAT</b> (dispatch + zone days), else the fixed {days}-day window (shown as an &ldquo;Aging&rdquo; highlight).
          </p>
          <CourierTatManager />

          <div className="border-t border-border pt-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-sm">Count working days only</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">TAT days skip weekly-off days &amp; holidays instead of calendar days.</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox data-testid="settings-working-days" checked={countWorking}
                          onCheckedChange={(v) => { setCountWorking(!!v); markDirty(); }} />
                <span className="text-sm text-muted-foreground">{countWorking ? "On" : "Off"}</span>
              </label>
            </div>
            {countWorking && (
              <>
                <div>
                  <Label className="text-sm">Weekly off days</Label>
                  <div className="flex flex-wrap gap-2 mt-1.5" data-testid="settings-weekly-off">
                    {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => {
                      const on = weeklyOff.includes(i);
                      return (
                        <button key={d} type="button" data-testid={`weekly-off-${i}`}
                                onClick={() => { setWeeklyOff((w) => on ? w.filter((x) => x !== i) : [...w, i]); markDirty(); }}
                                className={`text-xs font-medium rounded-full px-3 py-1 border transition-colors ${
                                  on ? "bg-[#0B3D3D] text-white border-[#0B3D3D]" : "bg-white text-muted-foreground border-border hover:border-[#0B3D3D]"
                                }`}>{d}</button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Holidays (excluded from TAT)</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Input type="date" data-testid="settings-holiday-input" value={newHoliday}
                           onChange={(e) => setNewHoliday(e.target.value)} className="max-w-[180px]" />
                    <Button type="button" variant="outline" size="sm" data-testid="settings-holiday-add"
                            onClick={() => { if (newHoliday && !holidays.includes(newHoliday)) { setHolidays((h) => [...h, newHoliday].sort()); setNewHoliday(""); markDirty(); } }}
                            className="gap-1"><Plus className="h-3.5 w-3.5" /> Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="settings-holiday-list">
                    {holidays.length === 0 ? <span className="text-[11px] text-muted-foreground">No holidays added.</span> :
                      holidays.map((h) => (
                        <span key={h} className="inline-flex items-center gap-1 text-xs bg-[#F4EFA1] text-[#5b4b00] rounded-full px-2.5 py-1">
                          {h}
                          <button type="button" data-testid={`holiday-remove-${h}`} onClick={() => { setHolidays((arr) => arr.filter((x) => x !== h)); markDirty(); }}
                                  className="hover:text-[#C24127]"><Trash2 className="h-3 w-3" /></button>
                        </span>
                      ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        <section id="couriers" className="scroll-mt-40 rounded-lg border border-border bg-white p-6 space-y-4">
          <div className="flex items-center gap-2 text-[#0B3D3D]">
            <Truck className="h-5 w-5" />
            <h3 className="font-heading font-bold text-[#111411]">Courier partners</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Manage the courier dropdown used when adding shipments, and set each courier&apos;s tracking-link format.
          </p>
          <CourierManager />
          <div className="pt-2 border-t border-border">
            <Label>Fallback tracking page (ClickPost)</Label>
            <Input data-testid="settings-fallback-url" value={fallbackUrl} onChange={(e) => { setFallbackUrl(e.target.value); markDirty(); }}
                   placeholder="https://track.girorganic.org/?awb={awb}" className="mt-1.5" />
            <p className="text-xs text-muted-foreground mt-1.5">
              Used when a courier has no tracking link and isn&apos;t a known carrier. Add your website&apos;s ClickPost
              tracking page and use <code className="bg-[#F4F6F4] px-1 rounded">{"{awb}"}</code> or{" "}
              <code className="bg-[#F4F6F4] px-1 rounded">{"{order_id}"}</code>. Saved with &ldquo;Save changes&rdquo; at the top.
            </p>
          </div>
        </section>

        <section id="countries" className="scroll-mt-40 rounded-lg border border-border bg-white p-6 space-y-4">
          <div className="flex items-center gap-2 text-[#0B3D3D]">
            <Globe className="h-5 w-5" />
            <h3 className="font-heading font-bold text-[#111411]">Countries</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Manage the country dropdown used when adding shipments. Country is required; the PIN code then auto-fills city and state.
          </p>
          <CountryManager />
        </section>

        <section id="final-actions" className="scroll-mt-40 rounded-lg border border-border bg-white p-6 space-y-4">
          <div className="flex items-center gap-2 text-[#0B3D3D]">
            <ListChecks className="h-5 w-5" />
            <h3 className="font-heading font-bold text-[#111411]">Final actions required</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Manage the list of possible final actions a team member must pick for each shipment (e.g. Priority Delivery,
            Mark as RTO). These describe what to do next. Tick &ldquo;Triggers new shipment&rdquo; on any final action
            that should queue a replacement (it also works on Shipment status).
          </p>
          <StatusManager onChanged={reloadStatuses} />
        </section>

        <section id="shipment-status" className="scroll-mt-40 rounded-lg border border-border bg-white p-6 space-y-4">
          <div className="flex items-center gap-2 text-[#0B3D3D]">
            <ListChecks className="h-5 w-5" />
            <h3 className="font-heading font-bold text-[#111411]">Shipment status</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            The workflow status of a shipment (e.g. Open, Resolved). Marking a shipment with a status that &ldquo;counts as
            resolved&rdquo; stops it counting toward overdue. This is separate from the final action required.
          </p>
          <StageManager />
        </section>

        <section id="roles" className="scroll-mt-40 rounded-lg border border-border bg-white p-6 space-y-4">
          <div className="flex items-center gap-2 text-[#0B3D3D]">
            <ShieldCheck className="h-5 w-5" />
            <h3 className="font-heading font-bold text-[#111411]">Roles &amp; access</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Create your own roles for the team (e.g. Customer Support Team, Manager). Choose whether each role is a
            team member (NDR Agent-level) or an admin. Roles appear when inviting members on the Team page.
          </p>
          <RolesManager />
        </section>

        <section id="whatsapp" className="scroll-mt-40 rounded-lg border border-border bg-white p-6 space-y-4">
          <div className="flex items-center gap-2 text-[#0B3D3D]">
            <MessageCircle className="h-5 w-5" />
            <h3 className="font-heading font-bold text-[#111411]">WhatsApp templates</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Set a message template per final action. Use variables{" "}
            <code className="text-xs bg-[#F4F6F4] px-1 rounded">{"{{courier_name}}"}</code>,{" "}
            <code className="text-xs bg-[#F4F6F4] px-1 rounded">{"{{AWBs}}"}</code>,{" "}
            <code className="text-xs bg-[#F4F6F4] px-1 rounded">{"{{order_ids}}"}</code>,{" "}
            <code className="text-xs bg-[#F4F6F4] px-1 rounded">{"{{status}}"}</code> and{" "}
            <code className="text-xs bg-[#F4F6F4] px-1 rounded">{"{{count}}"}</code> — they are filled in automatically
            when you send a follow-up.
          </p>
          <div className="space-y-3">
            {statuses.map((s) => (
              <div key={s.key}>
                <Label className="text-sm">{s.label}</Label>
                <Textarea data-testid={`wa-template-${s.key}`} rows={2} className="mt-1.5"
                          value={waTemplates[s.key] || ""}
                          onChange={(e) => { setWaTemplates((t) => ({ ...t, [s.key]: e.target.value })); markDirty(); }}
                          placeholder={"AWB - {{AWBs}} — arrange for " + s.label} />
              </div>
            ))}
            <div>
              <Label className="text-sm">Default template (used when a final action has no template)</Label>
              <Textarea data-testid="wa-template-default" rows={2} className="mt-1.5"
                        value={waTemplates.default || ""}
                        onChange={(e) => { setWaTemplates((t) => ({ ...t, default: e.target.value })); markDirty(); }}
                        placeholder={"AWB - {{AWBs}}\n\narrange for {{status}} for the above highlighted shipments"} />
            </div>
          </div>
        </section>

        <section id="alerts" className="scroll-mt-40 space-y-6">
          <div className="rounded-lg border border-border bg-white p-6 space-y-4">
            <div className="flex items-center gap-2 text-[#0B3D3D]">
              <Bell className="h-5 w-5" />
              <h3 className="font-heading font-bold text-[#111411]">Alerts &amp; Notifications</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Control the alert emails this panel sends to your inbox. In-app notifications (the bell) are unaffected.
            </p>
            <div className="flex items-center justify-between gap-4 rounded-md border border-border bg-[#F9F9F8] p-4">
              <div>
                <Label className="text-sm">Email me alerts</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5 max-w-md">
                  When off, you stop receiving the daily overdue digest and other alert emails from the NDR panel.
                  You&apos;ll still see them in your in-app bell.
                </p>
              </div>
              <Switch data-testid="settings-mute-emails" checked={!muteEmails} onCheckedChange={toggleEmails} />
            </div>
            <p className="text-xs text-muted-foreground">
              Want per-event control (choose in-app vs email for each event)? Manage it in your{" "}
              <a href="/profile" className="text-[#0B3D3D] font-medium hover:underline">Profile → Notifications</a>.
            </p>
          </div>

          <div className="rounded-lg border border-border bg-white p-6 space-y-5">
            <div className="flex items-center gap-2 text-[#0B3D3D]">
              <Bell className="h-5 w-5" />
              <h3 className="font-heading font-bold text-[#111411]">Overdue alerts</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              A daily automated check (9:30am IST) emails team members (who have alert emails on) and pings Microsoft
              Teams with shipments that are overdue or about to breach their deadline.
            </p>
            <div className="max-w-xs">
              <Label>Alert when deadline is within (days)</Label>
              <Input type="number" min={0} data-testid="settings-alert-within" value={alertWithin}
                     onChange={(e) => { setAlertWithin(e.target.value); markDirty(); }} className="mt-1.5" />
            </div>
            <div>
              <Label>Microsoft Teams Incoming Webhook URL (optional)</Label>
              <Input data-testid="settings-teams" value={teams} onChange={(e) => { setTeams(e.target.value); markDirty(); }}
                     placeholder="https://outlook.office.com/webhook/…" className="mt-1.5" />
              <p className="text-xs text-muted-foreground mt-1">
                In Teams: channel ••• → Connectors → Incoming Webhook → create → copy the URL here.
              </p>
            </div>
            <Button variant="outline" onClick={sendTest} disabled={testing} data-testid="settings-test-alert" className="gap-2">
              <Bell className="h-4 w-4" /> Send test alert now
            </Button>
          </div>
        </section>

        <section id="microsoft" className="scroll-mt-40 rounded-lg border border-border bg-white p-6 space-y-3">
          <div className="flex items-center gap-2 text-[#0B3D3D]">
            <ShieldCheck className="h-5 w-5" />
            <h3 className="font-heading font-bold text-[#111411]">Microsoft 365 sign-in</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            To let your team sign in with Microsoft work accounts, register an app in the Microsoft Entra admin center
            and share these 3 values with us — we'll enable the "Continue with Microsoft" button:
          </p>
          <ol className="text-sm text-[#111411] list-decimal ml-5 space-y-1">
            <li>Go to <span className="font-medium">entra.microsoft.com</span> → Identity → Applications → App registrations → New registration.</li>
            <li>Name it "GirOrganic NDR", choose <span className="font-medium">Accounts in this organizational directory only</span>, Register.</li>
            <li>From <span className="font-medium">Overview</span>, copy the <span className="font-medium">Application (client) ID</span> and <span className="font-medium">Directory (tenant) ID</span>.</li>
            <li>Authentication → Add platform → Web → Redirect URI:
              <code className="block mt-1 text-xs bg-[#F4F6F4] p-2 rounded break-all">{process.env.REACT_APP_BACKEND_URL}/api/auth/microsoft/callback</code>
            </li>
            <li>API permissions → Microsoft Graph → Delegated → add <span className="font-medium">User.Read, openid, profile, email</span> → Grant admin consent.</li>
            <li>Certificates &amp; secrets → New client secret → copy the <span className="font-medium">Value</span> (shown once).</li>
          </ol>
          <a href="https://entra.microsoft.com" target="_blank" rel="noreferrer"
             className="text-sm text-[#0B3D3D] font-medium inline-flex items-center gap-1 hover:underline">
            Open Microsoft Entra admin center <ExternalLink className="h-3 w-3" />
          </a>
        </section>
      </div>
    </div>
  );
}

function CountryManager() {
  const [countries, setCountries] = useState([]);
  const [name, setName] = useState("");
  const [aliases, setAliases] = useState("");
  const [edits, setEdits] = useState({});
  const load = () => api.get("/countries").then((r) => setCountries(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!name.trim()) { toast.error("Country name required"); return; }
    try {
      await api.post("/countries", { name: name.trim(), aliases: aliases.split(",").map((a) => a.trim()).filter(Boolean) });
      toast.success("Country added"); setName(""); setAliases(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to add country"); }
  };
  const saveAliases = async (c) => {
    const raw = edits[c.id] ?? (c.aliases || []).join(", ");
    try {
      await api.put(`/countries/${c.id}`, { aliases: raw.split(",").map((a) => a.trim()).filter(Boolean) });
      toast.success("Short codes saved"); load();
    } catch { toast.error("Failed to save"); }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/countries/${id}`);
      toast.success("Country removed"); load();
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm(`${e.response.data.detail}\n\nRemove it from the dropdown anyway?`)) {
          await api.delete(`/countries/${id}?force=true`); toast.success("Country removed"); load();
        }
      } else { toast.error("Failed to delete country"); }
    }
  };
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-2 flex-wrap rounded-md border border-border bg-[#F9F9F8] p-3">
        <div className="flex-1 min-w-[160px]">
          <Label>Country name</Label>
          <Input data-testid="country-name" value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="e.g. United States of America" className="mt-1.5" />
        </div>
        <div className="flex-1 min-w-[160px]">
          <Label>Short codes (comma-separated)</Label>
          <Input data-testid="country-aliases" value={aliases} onChange={(e) => setAliases(e.target.value)}
                 placeholder="e.g. US, USA" className="mt-1.5" />
        </div>
        <Button onClick={add} data-testid="country-add" className="gap-2 bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">
          <Plus className="h-4 w-4" /> Add
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        On import, uploaded country values (e.g. &ldquo;I&rdquo;, &ldquo;USA&rdquo;) are auto-matched to the full name here so the PIN
        can fill city &amp; state. Common codes (I/IN → India, US/USA → United States) are recognised automatically.
      </p>
      {countries.length > 0 ? (
        <div className="space-y-2">
          {countries.map((c) => {
            const val = edits[c.id] ?? (c.aliases || []).join(", ");
            return (
              <div key={c.id} data-testid={`country-row-${c.id}`}
                   className="flex items-center gap-2 flex-wrap rounded-md border border-border bg-white p-2.5">
                <span className="font-medium text-[#111411] min-w-[140px]">{c.name}</span>
                <Input data-testid={`country-aliases-${c.id}`} value={val}
                       onChange={(e) => setEdits((s) => ({ ...s, [c.id]: e.target.value }))}
                       placeholder="Short codes, comma-separated" className="h-9 flex-1 min-w-[160px]" />
                <Button size="sm" variant="outline" data-testid={`country-save-${c.id}`} onClick={() => saveAliases(c)} className="h-9">Save</Button>
                <button onClick={() => remove(c.id)} data-testid={`country-del-${c.id}`} className="text-[#C24127] hover:text-[#9e3420] px-1">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No countries yet. Add one above.</p>
      )}
    </div>
  );
}

function CourierManager() {
  const [couriers, setCouriers] = useState([]);
  const [name, setName] = useState("");
  const [tmpl, setTmpl] = useState("");
  const [edits, setEdits] = useState({});

  const load = () => api.get("/couriers").then((r) => setCouriers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) { toast.error("Courier name required"); return; }
    try {
      await api.post("/couriers", { name: name.trim(), tracking_url_template: tmpl.trim() });
      toast.success("Courier added");
      setName(""); setTmpl(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const saveTmpl = async (c) => {
    try {
      const r = await api.put(`/couriers/${c.id}`, { tracking_url_template: edits[c.id] ?? c.tracking_url_template ?? "" });
      toast.success(`Tracking link saved — ${r.data?.shipments_relinked ?? 0} shipment(s) updated`);
      setEdits((e) => { const n = { ...e }; delete n[c.id]; return n; });
      load();
    } catch (e) { toast.error("Failed to save tracking link"); }
  };
  const rebuildAll = async () => {
    try {
      const r = await api.post("/couriers/rebuild-links");
      toast.success(`Rebuilt ${r.data?.shipments_relinked ?? 0} tracking link(s)`);
    } catch { toast.error("Failed to rebuild links"); }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/couriers/${id}`);
      load();
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm(`${e.response.data.detail}\n\nExisting shipments keep this courier name and are NOT deleted. Remove it from the dropdown anyway?`)) {
          await api.delete(`/couriers/${id}?force=true`);
          toast.success("Courier removed");
          load();
        }
      } else {
        toast.error("Failed to delete courier");
      }
    }
  };

  return (
    <div className="space-y-5">
      <div className="border border-border rounded-lg p-4 bg-[#F9F9F8] space-y-3">
        <p className="text-xs font-medium text-muted-foreground">Add a courier</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[160px]">
            <Label>Courier name</Label>
            <Input data-testid="courier-name" value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. Delhivery" className="mt-1.5" />
          </div>
          <div className="flex-[2] min-w-[240px]">
            <Label>Sample tracking link <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input data-testid="courier-template" value={tmpl} onChange={(e) => setTmpl(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                   placeholder="https://track.delhivery.com/p/{awb}" className="mt-1.5" />
          </div>
          <Button onClick={add} data-testid="courier-add" className="gap-2 bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Paste a real tracking link and replace the tracking number with{" "}
          <code className="bg-white border border-border px-1 rounded">{"{awb}"}</code>. You can also use{" "}
          <code className="bg-white border border-border px-1 rounded">{"{order_id}"}</code>. Every shipment on this
          courier then gets a working link automatically.
        </p>
      </div>

      {couriers.length > 0 ? (
        <div className="space-y-2">
          {couriers.map((c) => {
            const val = edits[c.id] ?? c.tracking_url_template ?? "";
            const dirty = edits[c.id] !== undefined && edits[c.id] !== (c.tracking_url_template ?? "");
            return (
              <div key={c.id} data-testid={`courier-row-${c.id}`}
                   className="flex flex-wrap items-center gap-3 border border-border rounded-lg px-3 py-2 bg-white">
                <span className="text-sm font-medium text-[#111411] min-w-[120px]">{c.name}</span>
                <Input data-testid={`courier-template-${c.id}`} value={val}
                       onChange={(e) => setEdits((s) => ({ ...s, [c.id]: e.target.value }))}
                       placeholder="No tracking link — uses fallback. e.g. https://…/p/{awb}"
                       className="flex-1 min-w-[220px] h-9 text-sm" />
                {dirty && (
                  <Button size="sm" variant="outline" data-testid={`courier-save-${c.id}`} onClick={() => saveTmpl(c)} className="h-9">
                    Save link
                  </Button>
                )}
                <button onClick={() => remove(c.id)} data-testid={`courier-del-${c.id}`}
                        className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-[#F0D8C4] text-[#C24127]">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No couriers yet. Add one above.</p>
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
        <p className="text-xs text-muted-foreground">
          Saving a link auto-updates existing shipments of that courier. Use &ldquo;Rebuild all&rdquo; to refresh every
          shipment&apos;s link (e.g. after changing the fallback page).
        </p>
        <Button size="sm" variant="outline" data-testid="courier-rebuild-links" onClick={rebuildAll} className="gap-2">
          <RefreshCw className="h-4 w-4" /> Rebuild all tracking links
        </Button>
      </div>
    </div>
  );
}

function RolesManager() {
  const [roles, setRoles] = useState([]);
  const [label, setLabel] = useState("");
  const [level, setLevel] = useState("agent");

  const load = () => api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!label.trim()) { toast.error("Role name required"); return; }
    try {
      await api.post("/roles", { label: label.trim(), is_admin: level === "admin" });
      toast.success("Role created");
      setLabel(""); setLevel("agent"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const remove = async (r) => {
    try {
      await api.delete(`/roles/${r.key}`);
      toast.success("Role deleted"); load();
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm(`${e.response.data.detail}`)) {
          await api.delete(`/roles/${r.key}?force=true`);
          toast.success("Role deleted"); load();
        }
      } else { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    }
  };

  return (
    <div className="space-y-5">
      <div className="border border-border rounded-lg p-4 bg-[#F9F9F8] space-y-3">
        <p className="text-xs font-medium text-muted-foreground">Create a role</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <Label>Role name</Label>
            <Input data-testid="role-name" value={label} onChange={(e) => setLabel(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                   placeholder="e.g. Customer Support Team" className="mt-1.5" />
          </div>
          <div className="min-w-[200px]">
            <Label>Access level</Label>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger data-testid="role-level" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="agent">Team member (like NDR Agent)</SelectItem>
                <SelectItem value="admin">Admin (full access)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={add} data-testid="role-add" className="gap-2 bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">
            <Plus className="h-4 w-4" /> Add role
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          <b>Team member</b> = same permissions as an NDR Agent (shipments, comments, inbox).{" "}
          <b>Admin</b> = full access including Settings &amp; Team.
        </p>
      </div>

      <div className="space-y-2">
        {roles.map((r) => (
          <div key={r.key} data-testid={`role-row-${r.key}`}
               className="flex flex-wrap items-center gap-3 border border-border rounded-lg px-3 py-2.5 bg-white">
            <span className="text-sm font-medium text-[#111411] min-w-[160px]">{r.label}</span>
            <span className={`text-xs rounded-full px-2 py-0.5 border ${r.is_admin ? "bg-[#E8ECE8] text-[#0B3D3D] border-[#CFDDD0]" : "bg-[#F4F6F4] text-[#5C635C] border-border"}`}>
              {r.is_admin ? "Admin" : "Team member"}
            </span>
            {r.is_system && <span className="text-[11px] text-muted-foreground">System role</span>}
            <div className="ml-auto">
              {!r.is_system && (
                <button onClick={() => remove(r)} data-testid={`role-del-${r.key}`}
                        className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-[#F0D8C4] text-[#C24127]">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const STATUS_COLORS = [
  { key: "amber", cls: "bg-[#FBEEE6] text-[#B45309] border-[#F0D8C4]" },
  { key: "green", cls: "bg-[#E8ECE8] text-[#0B3D3D] border-[#CFDDD0]" },
  { key: "emerald", cls: "bg-[#E3F0E5] text-[#1E7A34] border-[#C4E0C9]" },
  { key: "red", cls: "bg-[#F7E4DF] text-[#C24127] border-[#EBC7BE]" },
  { key: "blue", cls: "bg-[#E4ECF7] text-[#1D4E89] border-[#C6D6EC]" },
  { key: "purple", cls: "bg-[#EEE7F7] text-[#6B3FA0] border-[#D8C9EC]" },
  { key: "gray", cls: "bg-[#ECECEC] text-[#5C635C] border-[#DADADA]" },
];

function StatusManager({ onChanged }) {
  const { statuses } = useStatuses();
  const [label, setLabel] = useState("");
  const [color, setColor] = useState("blue");
  const [resolved, setResolved] = useState(false);
  const [triggersReship, setTriggersReship] = useState(false);
  const [triggersRvp, setTriggersRvp] = useState(false);
  const [autoResolve, setAutoResolve] = useState(false);
  const [direction, setDirection] = useState("forward");
  const [edits, setEdits] = useState({});

  const getRow = (s) => edits[s.key] ?? { label: s.label, color: s.color, resolved: s.resolved, triggers_reship: s.triggers_reship, triggers_rvp: s.triggers_rvp, auto_resolve: s.auto_resolve, direction: s.direction || "forward" };
  const setRow = (key, patch) => setEdits((e) => ({ ...e, [key]: { ...getRowFor(key), ...patch } }));
  const getRowFor = (key) => {
    if (edits[key]) return edits[key];
    const s = statuses.find((x) => x.key === key);
    return { label: s.label, color: s.color, resolved: s.resolved, triggers_reship: s.triggers_reship, triggers_rvp: s.triggers_rvp, auto_resolve: s.auto_resolve, direction: s.direction || "forward" };
  };
  const isDirty = (s) => {
    const e = edits[s.key];
    if (!e) return false;
    return e.label !== s.label || e.color !== s.color || e.resolved !== s.resolved || e.triggers_reship !== s.triggers_reship || e.triggers_rvp !== s.triggers_rvp || e.auto_resolve !== s.auto_resolve || e.direction !== (s.direction || "forward");
  };

  const add = async () => {
    if (!label.trim()) { toast.error("Name required"); return; }
    try {
      await api.post("/statuses", { label: label.trim(), color, resolved, triggers_reship: triggersReship, triggers_rvp: triggersRvp, auto_resolve: autoResolve, direction });
      toast.success("Final action added");
      setLabel(""); setColor("blue"); setResolved(false); setTriggersReship(false); setTriggersRvp(false); setAutoResolve(false); setDirection("forward");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const saveRow = async (s) => {
    const r = getRow(s);
    if (!r.label.trim()) { toast.error("Name required"); return; }
    try {
      await api.put(`/statuses/${s.key}`, { label: r.label.trim(), color: r.color, resolved: r.resolved, triggers_reship: r.triggers_reship, triggers_rvp: r.triggers_rvp, auto_resolve: r.auto_resolve, direction: r.direction });
      toast.success("Status updated");
      setEdits((e) => { const n = { ...e }; delete n[s.key]; return n; });
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to update"); }
  };
  const remove = async (s) => {
    try {
      await api.delete(`/statuses/${s.key}`);
      toast.success("Status removed");
      onChanged?.();
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm(`${e.response.data.detail}`)) {
          await api.delete(`/statuses/${s.key}?force=true`);
          toast.success("Status removed");
          onChanged?.();
        }
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to remove");
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {statuses.map((s) => {
          const r = getRow(s);
          return (
            <div key={s.key} data-testid={`status-row-${s.key}`}
                 className="flex flex-wrap items-center gap-3 border border-border rounded-lg px-3 py-2 bg-white">
              <Input data-testid={`status-label-${s.key}`} value={r.label}
                     onChange={(e) => setRow(s.key, { label: e.target.value })}
                     className="h-9 text-sm w-44" />
              <Select value={r.color} onValueChange={(v) => setRow(s.key, { color: v })}>
                <SelectTrigger data-testid={`status-color-${s.key}`} className="h-9 w-28 capitalize text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_COLORS.map((c) => <SelectItem key={c.key} value={c.key} className="capitalize">{c.key}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={r.direction || "forward"} onValueChange={(v) => setRow(s.key, { direction: v })}>
                <SelectTrigger data-testid={`status-direction-${s.key}`} className="h-9 w-28 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="forward">Forward</SelectItem>
                  <SelectItem value="return">Return</SelectItem>
                </SelectContent>
              </Select>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <Checkbox checked={!!r.resolved} onCheckedChange={(v) => setRow(s.key, { resolved: !!v })} data-testid={`status-resolved-${s.key}`} />
                Resolved
              </label>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <Checkbox checked={!!r.auto_resolve} onCheckedChange={(v) => setRow(s.key, { auto_resolve: !!v })} data-testid={`status-autoresolve-${s.key}`} />
                Auto-resolve
              </label>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <Checkbox checked={!!r.triggers_reship} onCheckedChange={(v) => setRow(s.key, { triggers_reship: !!v })} data-testid={`status-reship-${s.key}`} />
                Triggers new shipment
              </label>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <Checkbox checked={!!r.triggers_rvp} onCheckedChange={(v) => setRow(s.key, { triggers_rvp: !!v })} data-testid={`status-rvp-${s.key}`} />
                Triggers reverse pickup
              </label>
              {s.is_system && <span className="text-[10px] text-muted-foreground">built-in</span>}
              <div className="ml-auto flex items-center gap-2">
                {isDirty(s) && (
                  <Button size="sm" variant="outline" data-testid={`status-save-${s.key}`} onClick={() => saveRow(s)} className="h-8">Save</Button>
                )}
                <button onClick={() => remove(s)} data-testid={`status-del-${s.key}`}
                        className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-[#F0D8C4] text-[#C24127]">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border border-border rounded-lg p-4 space-y-3 bg-[#F9F9F8]">
        <p className="text-xs font-medium text-muted-foreground">Add a final action</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <Label>Final action name</Label>
            <Input data-testid="status-name" value={label} onChange={(e) => setLabel(e.target.value)}
                   placeholder="e.g. Priority Delivery" className="mt-1.5" />
          </div>
          <div className="min-w-[140px]">
            <Label>Colour</Label>
            <Select value={color} onValueChange={setColor}>
              <SelectTrigger data-testid="status-color" className="mt-1.5 capitalize"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_COLORS.map((c) => <SelectItem key={c.key} value={c.key} className="capitalize">{c.key}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[150px]">
            <Label>Direction</Label>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger data-testid="status-direction" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="forward">Forward</SelectItem>
                <SelectItem value="return">Return (RTO)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap gap-5">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={resolved} onCheckedChange={(v) => setResolved(!!v)} data-testid="status-resolved" />
            Counts as resolved (not overdue)
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={autoResolve} onCheckedChange={(v) => setAutoResolve(!!v)} data-testid="status-autoresolve" />
            Auto-resolve (picking this sets Status to Resolved)
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={triggersReship} onCheckedChange={(v) => setTriggersReship(!!v)} data-testid="status-reship" />
            Triggers new shipment (queues a replacement)
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={triggersRvp} onCheckedChange={(v) => setTriggersRvp(!!v)} data-testid="status-rvp" />
            Triggers reverse pickup (opens RVP form)
          </label>
        </div>
        <Button onClick={add} data-testid="status-add" className="gap-2 bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">
          <Plus className="h-4 w-4" /> Add final action
        </Button>
      </div>
    </div>
  );
}

function StageManager() {
  const { stages, reloadStages } = useStatuses();
  const [label, setLabel] = useState("");
  const [color, setColor] = useState("blue");
  const [resolved, setResolved] = useState(false);
  const [triggersReship, setTriggersReship] = useState(false);
  const [edits, setEdits] = useState({});

  const getRow = (s) => edits[s.key] ?? { label: s.label, color: s.color, resolved: s.resolved, triggers_reship: s.triggers_reship };
  const setRow = (key, patch) => setEdits((e) => {
    const base = e[key] ?? (() => { const s = stages.find((x) => x.key === key); return { label: s.label, color: s.color, resolved: s.resolved, triggers_reship: s.triggers_reship }; })();
    return { ...e, [key]: { ...base, ...patch } };
  });
  const isDirty = (s) => { const e = edits[s.key]; return !!e && (e.label !== s.label || e.color !== s.color || e.resolved !== s.resolved || e.triggers_reship !== s.triggers_reship); };

  const add = async () => {
    if (!label.trim()) { toast.error("Name required"); return; }
    try {
      await api.post("/stages", { label: label.trim(), color, resolved, triggers_reship: triggersReship });
      toast.success("Status added");
      setLabel(""); setColor("blue"); setResolved(false); setTriggersReship(false); reloadStages();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const saveRow = async (s) => {
    const r = getRow(s);
    if (!r.label.trim()) { toast.error("Name required"); return; }
    try {
      await api.put(`/stages/${s.key}`, { label: r.label.trim(), color: r.color, resolved: r.resolved, triggers_reship: r.triggers_reship });
      toast.success("Status updated");
      setEdits((e) => { const n = { ...e }; delete n[s.key]; return n; });
      reloadStages();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to update"); }
  };
  const remove = async (s) => {
    try {
      await api.delete(`/stages/${s.key}`);
      toast.success("Status removed"); reloadStages();
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm(`${e.response.data.detail}`)) {
          await api.delete(`/stages/${s.key}?force=true`);
          toast.success("Status removed"); reloadStages();
        }
      } else { toast.error(formatApiError(e.response?.data?.detail) || "Failed to remove"); }
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {stages.map((s) => {
          const r = getRow(s);
          return (
            <div key={s.key} data-testid={`stage-row-${s.key}`}
                 className="flex flex-wrap items-center gap-3 border border-border rounded-lg px-3 py-2 bg-white">
              <Input data-testid={`stage-label-${s.key}`} value={r.label}
                     onChange={(e) => setRow(s.key, { label: e.target.value })}
                     className="h-9 text-sm w-44" />
              <Select value={r.color} onValueChange={(v) => setRow(s.key, { color: v })}>
                <SelectTrigger data-testid={`stage-color-${s.key}`} className="h-9 w-28 capitalize text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_COLORS.map((c) => <SelectItem key={c.key} value={c.key} className="capitalize">{c.key}</SelectItem>)}
                </SelectContent>
              </Select>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <Checkbox checked={!!r.resolved} disabled={s.is_system}
                          onCheckedChange={(v) => setRow(s.key, { resolved: !!v })} data-testid={`stage-resolved-${s.key}`} />
                Counts as resolved
              </label>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <Checkbox checked={!!r.triggers_reship}
                          onCheckedChange={(v) => setRow(s.key, { triggers_reship: !!v })} data-testid={`stage-reship-${s.key}`} />
                Triggers new shipment
              </label>
              {s.is_system && <span className="text-[10px] text-muted-foreground">built-in</span>}
              <div className="ml-auto flex items-center gap-2">
                {isDirty(s) && (
                  <Button size="sm" variant="outline" data-testid={`stage-save-${s.key}`} onClick={() => saveRow(s)} className="h-8">Save</Button>
                )}
                {!s.is_system && (
                  <button onClick={() => remove(s)} data-testid={`stage-del-${s.key}`}
                          className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-[#F0D8C4] text-[#C24127]">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="border border-border rounded-lg p-4 space-y-3 bg-[#F9F9F8]">
        <p className="text-xs font-medium text-muted-foreground">Add a status</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <Label>Status name</Label>
            <Input data-testid="stage-name" value={label} onChange={(e) => setLabel(e.target.value)}
                   placeholder="e.g. In Progress" className="mt-1.5" />
          </div>
          <div className="min-w-[140px]">
            <Label>Colour</Label>
            <Select value={color} onValueChange={setColor}>
              <SelectTrigger data-testid="stage-color-new" className="mt-1.5 capitalize"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_COLORS.map((c) => <SelectItem key={c.key} value={c.key} className="capitalize">{c.key}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={resolved} onCheckedChange={(v) => setResolved(!!v)} data-testid="stage-resolved-new" />
          Counts as resolved (stops the overdue timer)
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={triggersReship} onCheckedChange={(v) => setTriggersReship(!!v)} data-testid="stage-reship-new" />
          Triggers new shipment (prompts for new shipment details when picked)
        </label>
        <Button onClick={add} data-testid="stage-add" className="gap-2 bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">
          <Plus className="h-4 w-4" /> Add status
        </Button>
      </div>
    </div>
  );
}



